import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedFetch } from './useAuth'

export type Role = 'viewer' | 'editor' | 'moderator' | 'owner'
export type DiagramAccessLink = { id: string; token: string; role: Role; scope: string; expiresAt?: string | null; revokedAt?: string | null; createdAt: string }

export function useDiagramLinks(diagramId?: string) {
  const fetcher = useAuthenticatedFetch()
  const qc = useQueryClient()
  const enabled = Boolean(diagramId)

  const links = useQuery({
    queryKey: ['diagram', diagramId, 'links'],
    enabled,
    // Mutations below invalidate this key; remounts should reuse the cache
    // instead of refetching on every shell re-render.
    staleTime: 60_000,
    queryFn: async (): Promise<DiagramAccessLink[]> => {
      const r = await fetcher(`/api/diagrams/${diagramId}/links`)
      if (!r.ok) throw new Error('Failed to load access links')
      const j = await r.json()
      return (j.links || []) as DiagramAccessLink[]
    },
  })

  const createLink = useMutation({
    mutationFn: async (payload: { role: Role; scope: string; expiresAt?: string | null }) => {
      const r = await fetcher(`/api/diagrams/${diagramId}/links`, {
        method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(payload)
      })
      if (!r.ok) throw new Error('Failed to create link')
      return (await r.json()).link as DiagramAccessLink
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'links'] })
      qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'access'] })
    },
  })

  const revokeLink = useMutation({
    mutationFn: async (linkId: string) => {
      const r = await fetcher(`/api/diagrams/${diagramId}/links/${encodeURIComponent(linkId)}/revoke`, { method: 'POST' })
      if (!r.ok) throw new Error('Failed to revoke link')
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'links'] })
  })

  const deleteLink = useMutation({
    mutationFn: async (linkId: string) => {
      const r = await fetcher(`/api/diagrams/${diagramId}/links/${encodeURIComponent(linkId)}`, { method: 'DELETE' })
      if (!r.ok) throw new Error('Failed to delete link')
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['diagram', diagramId, 'links'] })
  })

  return { links, createLink, revokeLink, deleteLink }
}

